<template>
    <div>
      <TsBreadcrumb
          :data="breadcrumb_links"
          data-testid="brand-listing-page-breadcrumb"
          root-class="mx-auto max-w-screen-xl px-4"
        />
      <TsLoadingCircularSpinner v-if="loading" />
      <TsSection v-else>
        <div
          class="max-md:w-full flex flex-row-reverse md:flex-col relative mt-3"
        >
          <div class="max-md:max-w-max md:w-full">
            <TsSearchWithAlphabetFilter
              @input-search="(q) => handleSearchFromInput(q)"
              @filter-search="(q) => handleAlphabeticSearch(q)"
              data-testid="brand-listing-page-search-with-alphabet"
              :data-to-filter-array="
                extractValues(
                  data.map((item) => item.brand),
                  'title'
                )
              "
              :placeholder="useTranslation('searchBrands', 'Zoek merken')"
              :show-all-button="true"
              :show-search-bar="true"
            >
            </TsSearchWithAlphabetFilter>
          </div>
  
          <div
            class="max-md:w-full max-md:max-h-[700px] max-md:overflow-y-auto max-md:hide-scroll-bar"
          >
            <TsPopularBrandSection
              :brand-data="filteredBrands"
              @brand="(brand :any ) => handleBrandCardClick(brand)"
            />
          </div>
        </div>
      </TsSection>
    </div>
  </template>  
  
  <script setup lang="ts">
  import { useTranslation } from '~/composables/useTranslation';
  const TsBreadcrumb = defineAsyncComponent(() => import('~/components/molecules/TsBreadcrumb.vue'));
  const TsLoadingCircularSpinner = defineAsyncComponent(() => import('~/components/organisms/TsLoadingCircularSpinner.vue'));
  const TsSearchWithAlphabetFilter = defineAsyncComponent(() => import('~/components/organisms/TsSearchWithAlphabetFilter.vue'));
  const TsPopularBrandSection = defineAsyncComponent(() => import('~/components/organisms/TsPopularBrandSection.vue'));
  
  const brandStore = useBrandStore();
  const filteredBrands = ref<any>([]);
  const algoliaStore = useAlgoliaStore();
  const handleBrandCardClick = (brandName: string) => {
    algoliaStore.currentActiveBrand = brandName;
    brandStore.handleBrandCardClick(brandName);
    logEvent({
          options: {
            eventAction: "click",
            eventProperties: {
             brand:brandName,
            },
            eventInteraction: true,
            eventLabel: `User clicked on ${brandName} brand link`,
            eventName: "brand_navigation",
          },
        });
  };
  
  const builderStore = useBuilderStore();
  const loading = ref(true);
  
  const builderBrands = [];
  let brands = []
      let offset = 0;

      do {
        brands = await builderStore.getList("brands-list", {}, {}, 100, offset);
        builderBrands.push(...brands);
        offset += 100;
      } while(brands.length === 100)

  
  const fuse = ref();
  const data = builderBrands.map((item: any) => ({
    brand: {
      id: item.data.brand.id,
      image: item.data.brand.image,
      color: "white",
      tag: "popular",
      alt: item.data.brand.title,
      title: item.data.brand.title,
    },
  }));
  
  onMounted(async () => {
    const { default: Fuse } = await import('fuse.js');

    filteredBrands.value = data;
    loading.value = false;
  
    fuse.value = new Fuse(data, {
      keys: ["brand.title"], 
      threshold: 0.3, 
    });
  });
  
  function extractValues<T, K extends keyof T>(data: T[], key: K): T[K][] {
    return data.map((item) => item[key]);
  }
  
  const handleSearchFromInput = (q: string) => {
    if (q) {
      const fuseResults = fuse.value.search(q.trim());
  
      filteredBrands.value = fuseResults.map((result: any) => result.item);
    } else {
      filteredBrands.value = data;
    }
  };
  
  const handleAlphabeticSearch = (q: string) => {
    if (q) {
      filteredBrands.value = data.filter(
        (item: any) => item.brand.title[0].toLowerCase() === q.toLowerCase()
      );
    } else {
      filteredBrands.value = data;
    }
  };
  
  useHead({
    title: "Onze Merken",
    meta: [
      {
        name: "description",
        content:
          "Bosch DeWalt Festool Makita Stanley Heco Kopp Brennenstuhl Henco Heller Schneider Uponor Rubi Aco Spax GB",
      },
    ],
  });
  
  const breadcrumb_links = [
    {
      text: useTranslation("home", "Thuis"),
      link: "/",
    },
    {
      text: useTranslation("brandListing", "Merkvermelding"),
      link: getBrandSlug(),
    },
  ];
  
  schemaBrandListing(builderBrands);
  </script>
  